import { fileContentUrl } from '@/lib/files/urls';
/**
 * Shared browser-download helpers.
 *
 * Every canvas download path (rendered outputs, uploads, video frames, PDFs)
 * funnels through here so the fragile parts are fixed in exactly one place:
 *
 *  1. The anchor is appended to the DOM before `.click()`. A detached anchor is
 *     silently ignored by Firefox and some WebView/embedded contexts — the
 *     click resolves but no download starts.
 *  2. Object URLs are revoked on a delay, never synchronously after the click.
 *     Blob-URL downloads are asynchronous; revoking in the same tick cancels the
 *     still-pending download in Chromium/WebKit. This is the classic
 *     "download silently does nothing" bug, worst for large files.
 *
 * Keep these synchronous so callers can invoke them inside the user-gesture
 * chain (a download started from a stale gesture may be blocked).
 */

/** How long to keep an object URL alive after the click before revoking it. */
export const OBJECT_URL_REVOKE_DELAY_MS = 60_000;

/**
 * Triggers a browser download for an already-resolvable href (data URL, object
 * URL, or remote URL) by appending a temporary anchor, clicking it, and
 * removing it.
 */
export function triggerDownload(href: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = fileContentUrl(href);
  anchor.download = fileName;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Downloads a Blob by wrapping it in an object URL, triggering the download, and
 * revoking the URL after a delay so the browser has time to read the blob.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    triggerDownload(objectUrl, fileName);
  } finally {
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, OBJECT_URL_REVOKE_DELAY_MS);
  }
}
